import { useState } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_URL = 'https://readdy.ai/api/form/db95ud46oi3g3tv8ut20';
const CONTACT_EMAIL = 'contact@korea-ethics.com';

export default function About() {
  usePageMeta({
    title: '关于我们 - 韩国伦理影视推荐网站',
    description:
      '了解韩国伦理网站的定位、内容范围与使用须知，并通过联系表单或邮箱向我们反馈建议。本站专注韩国伦理影视剧情介绍与推荐。',
    keywords: '关于韩国伦理,韩国伦理网站,伦理影视推荐,联系我们',
  });

  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // Anti-spam honeypot: pretend success for bots without sending anything
    const honey = (data.get('contact_alt') as string | null)?.trim() || '';
    if (honey) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'contact_alt') return;
      if (typeof value === 'string' && value.trim()) payload.append(key, value);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交内容被识别为异常，请稍后重试。' : serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="pt-16">
      {/* Hero */}
      <div className="w-full px-4 md:px-8 py-12 md:py-16 bg-primary-950">
        <div className="max-w-4xl mx-auto text-center">
          <nav className="flex flex-wrap items-center justify-center gap-2 text-xs text-primary-300/80 mb-5" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">关于韩国伦理</h1>
          <p className="mt-4 text-sm md:text-base text-primary-200/85 max-w-2xl mx-auto leading-relaxed">
            一个专注韩国伦理影视内容展示与推荐的影视资讯网站，帮助观众了解、筛选并浏览优质的韩国伦理影视作品。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Intro */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-1 h-6 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
                </div>
                <p className="text-sm md:text-base text-foreground-700 leading-loose">
                  韩国伦理网站面向对韩国伦理影视题材感兴趣的观众群体。我们不做资源的搬运工，而是希望成为一座
                  干净、可靠的影视信息入口：把每一部作品的核心剧情、主创信息与分类标签整理清楚，让观众能在
                  有限的时间里，快速判断一部作品是否值得观看。
                </p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-1 h-6 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-xl font-bold text-foreground-950">内容范围</h2>
                </div>
                <p className="text-sm md:text-base text-foreground-700 leading-loose">
                  我们目前收录三大内容方向：以家族、孝道与宗族礼法为核心的传统伦理剧；聚焦都市家庭、婚姻与
                  代际冲突的现代伦理剧；以及以电影语言细腻描绘人性与伦理的伦理电影。每一部作品都配有分段的
                  剧情介绍，便于长时间阅读。
                </p>
                <ul className="mt-4 space-y-2 text-sm text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" /> 清晰的影视分类与题材筛选
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" /> 完整的剧情介绍与主创信息
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-accent-500 mt-0.5" /> 基于热度与评分的作品推荐
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="font-heading text-2xl font-bold text-primary-600">{movies.length}</div>
                <div className="mt-1 text-xs text-foreground-500">精选影视作品</div>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="font-heading text-2xl font-bold text-accent-600">3</div>
                <div className="mt-1 text-xs text-foreground-500">核心内容方向</div>
              </div>
              <div className="p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="font-heading text-2xl font-bold text-secondary-600">持续</div>
                <div className="mt-1 text-xs text-foreground-500">内容整理与更新</div>
              </div>
            </div>
          </section>

          {/* Contact + form */}
          <section className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">联系我们</h2>
              </div>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                如果你对我们的内容有建议，发现信息有误，或希望推荐一部值得收录的韩国伦理影视作品，
                欢迎通过以下方式与我们联系。
              </p>
              <div className="mt-5 p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="text-xs text-foreground-500 mb-2">联系邮箱</div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-heading text-base text-foreground-950">{CONTACT_EMAIL}</span>
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
                    {copied ? '已复制' : '复制邮箱'}
                  </button>
                </div>
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-foreground-500">
                <i className="ri-information-line mt-0.5" />
                <span>我们通常会在 2-3 个工作日内查看并回复你的反馈。</span>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1 h-6 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">反馈表单</h2>
              </div>

              {status === 'success' ? (
                <div className="p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xl">
                    <i className="ri-check-line" />
                  </div>
                  <p className="mt-4 text-foreground-900 font-medium">感谢你的反馈！</p>
                  <p className="mt-2 text-sm text-foreground-500">我们已收到你的留言，会尽快查看并回复。</p>
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-5 px-5 py-2 rounded-md border border-background-300 text-foreground-700 text-sm hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    再提交一条
                  </button>
                </div>
              ) : (
                <form data-readdy-form id="about-contact-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">你的称呼</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于我们回复你"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="topic" className="block text-sm text-foreground-700 mb-1.5">反馈类型</label>
                    <select
                      id="topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-300"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="影视推荐">影视推荐</option>
                      <option value="信息纠错">信息纠错</option>
                      <option value="合作洽谈">合作洽谈</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="请写下你的建议或想推荐的影视作品（最多 500 字）"
                      className="w-full px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                    />
                  </div>

                  {/* Honeypot field (hidden, not for real users) */}
                  <input
                    className="form-line-note"
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === 'error' && formError && (
                    <p className="text-sm text-accent-700 bg-accent-50 rounded-md px-3 py-2">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                  >
                    {status === 'submitting' ? '提交中...' : '提交反馈'}
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* Copyright */}
          <section className="mt-14 md:mt-20">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1 h-6 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950">版权声明与使用须知</h2>
            </div>
            <div className="p-6 rounded-lg bg-background-100 border border-background-200 space-y-3 text-sm text-foreground-600 leading-loose">
              <p>
                本站为影视资讯与推荐类演示网站，所有剧情介绍与作品信息仅用于内容展示与信息参考，
                <strong>不提供任何影视资源的在线播放或下载</strong>。
              </p>
              <p>
                站内作品名称、剧情简介等文字内容由本站整理与撰写，相关影视作品的著作权归其权利方所有。
                如涉及版权问题，请通过上方联系邮箱与我们联系，我们将及时处理。
              </p>
              <p>访问与使用本站内容即表示你已阅读并同意以上说明。</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}